import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActiveBadge, PriorityBadge } from '@/components/Badges';
import { EmptyState, LoadingState } from '@/components/States';
import { Plus, Search, Edit2, Trash2, Tags, Loader2, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';
import { PRIORITY_LABEL } from '@/lib/labels';

const EMPTY = { name: '', description: '', defaultPriority: 'MEDIUM', isCritical: false, isActive: true };

export default function CategoriesPage() {
  const { hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const load = useCallback(async () => {
    if (isSuperAdmin && !selectedCompany) {
      setItems([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = { pageSize: 100 };
      if (q) params.q = q;
      if (isSuperAdmin && selectedCompany) params.companyId = selectedCompany;
      const { data } = await api.get('/categories', { params });
      setItems(data.items);
    } catch { toast.error('Falha ao carregar categorias'); }
    finally { setLoading(false); }
  }, [q, isSuperAdmin, selectedCompany]);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/companies', { params: { page: 1, pageSize: 1000 } })
        .then(({ data }) => setCompanies(data.items || []))
        .catch(() => toast.error('Erro ao carregar empresas'));
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpenModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpenModal(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/categories/${editing.id}`, form);
      else await api.post('/categories', form);
      toast.success(editing ? 'Categoria atualizada' : 'Categoria criada');
      setOpenModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/categories/${confirmDel.id}`); toast.success('Categoria excluída'); setConfirmDel(null); load(); }
    catch { toast.error('Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Tags className="w-6 h-6 text-blue-600" /> Categorias</h1>
          <p className="text-sm text-slate-500">Tipos de chamados de garantia.</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" disabled={isSuperAdmin && !selectedCompany}><Plus className="w-4 h-4 mr-2" /> Nova Categoria</Button>
      </div>

      {isSuperAdmin && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Selecionar Empresa:</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Selecione uma empresa..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.tradeName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Buscar por nome..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
        </div>

        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nenhuma categoria" icon={Tags} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium">Descrição</th>
                  <th className="px-4 py-3 font-medium">Prioridade</th>
                  <th className="px-4 py-3 font-medium">Crítica</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{c.description || '-'}</td>
                    <td className="px-4 py-3"><PriorityBadge priority={c.defaultPriority} /></td>
                    <td className="px-4 py-3">{c.isCritical ? <span className="inline-flex items-center gap-1 text-xs text-red-600"><AlertOctagon className="w-3 h-3" /> Sim</span> : <span className="text-xs text-slate-400">Não</span>}</td>
                    <td className="px-4 py-3"><ActiveBadge active={c.isActive} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDel(c)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader><DialogTitle>{editing ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Descrição</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Prioridade Padrão</Label>
              <Select value={form.defaultPriority} onValueChange={(v) => setForm({ ...form, defaultPriority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRIORITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.isCritical} onCheckedChange={(v) => setForm({ ...form, isCritical: !!v })} />
              <span className="text-sm">Categoria crítica</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Salvar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Excluir categoria?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{confirmDel?.name} será desativada.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
